'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Check, CheckCircle2, Lightbulb } from 'lucide-react';
import { LazyImage } from '@/components/LazyImage';
import { ConciergeEnquiryButton } from '@/components/concierge/ConciergeEnquiryButton';
import { cn } from '@/lib/utils';
const TIM_HEADSHOT_URL = 'https://sizzlenzfiles.s3.ap-southeast-2.amazonaws.com/01a10442-96c9-736c-98fd-8edae73090dd.png';

/** Largest rendered headshot size in CSS px (sm:h-48), used for responsive `sizes`. */
const HEADSHOT_SIZE_PX = 192;

/**
 * Signed-out visitors sign in first, then land on the standalone /get-ideas
 * form page (mirrors the directory "get listed" flow).
 */
const DEFAULT_SIGN_IN_HREF = '/auth/signin?redirect=/get-ideas';
const DEFAULT_POINTS = ["It's free, and I'm independent and unbiased. Nobody pays me to recommend them.", "I'll share ideas and honest recommendations for your brand or store.", "If you need expert help, I'll introduce you to the right people."];
interface SizzleConciergeSectionProps {
  /** Headshot URL. Use a sizzlenzfiles URL so LazyImage resizes it via Cloudflare. */
  imageSrc?: string;
  name?: string;
  role?: string;
  heading?: string;
  description?: string;
  points?: string[];
  /** Label for the primary action that opens the request form. */
  enquiryLabel?: string;
  /** Where signed-out visitors go before they can send a request. */
  signInHref?: string;
  /**
   * When true (default), signed-out visitors must sign in before sending a
   * request. When false, they open the request dialog directly.
   */
  userSessionRequired?: boolean;
  /** Label + route for the "explore inspiration" action. */
  inspirationLabel?: string;
  inspirationHref?: string;
  /** Optional text link below the buttons, always an internal route. */
  tertiaryLabel?: string;
  tertiaryHref?: string;
  className?: string;
}

/**
 * Personal intro from the founder offering a free, independent service: brands
 * and retailers get ideas and recommendations, and are connected with the
 * best-fit expert help to grow demand. Signed-in users send a request via
 * {@link ConciergeEnquiryDialog}; signed-out users are sent to sign in first.
 */
export function SizzleConciergeSection(t0) {
  const $ = _c(43);
  const {
    imageSrc: t1,
    name: t2,
    role: t3,
    heading: t4,
    description: t5,
    points: t6,
    enquiryLabel: t7,
    signInHref: t8,
    userSessionRequired: t9,
    inspirationLabel: t10,
    inspirationHref: t11,
    tertiaryLabel: t12,
    tertiaryHref: t13,
    className
  } = t0;
  const imageSrc = t1 === undefined ? TIM_HEADSHOT_URL : t1;
  const name = t2 === undefined ? "Tim Shnaider" : t2;
  const role = t3 === undefined ? "Founder, Sizzle" : t3;
  const heading = t4 === undefined ? "Hi, I'm Tim. Let's figure out your next move together." : t4;
  const description = t5 === undefined ? "I started Sizzle to help food, beverage and consumer goods brands and retailers grow. If you're not sure what to try next, tell me what you're working on. I'll give you some ideas, and if you need a hand making them happen, I'll connect you with someone who can help." : t5;
  const points = t6 === undefined ? DEFAULT_POINTS : t6;
  const enquiryLabel = t7 === undefined ? "Get a free tailored analysis and suggestions" : t7;
  const signInHref = t8 === undefined ? DEFAULT_SIGN_IN_HREF : t8;
  const userSessionRequired = t9 === undefined ? true : t9;
  const inspirationLabel = t10 === undefined ? "Get Inspired" : t10;
  const inspirationHref = t11 === undefined ? "/inspiration-feed" : t11;
  const tertiaryLabel = t12 === undefined ? "Browse the Directory" : t12;
  const tertiaryHref = t13 === undefined ? "/directory" : t13;
  const [submitted, setSubmitted] = useState(false);
  let t14;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn("inline-flex cursor-pointer items-center justify-center gap-2 rounded-full px-7 py-3.5 text-base font-black shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl", "bg-[#ff5a36] text-white shadow-red-500/20 hover:shadow-red-500/30");
    $[0] = t14;
  } else {
    t14 = $[0];
  }
  const primaryClassName = t14;
  let t15;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("inline-flex cursor-pointer items-center justify-center gap-2 rounded-full px-7 py-3.5 text-base font-black shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl", "bg-gray-900 text-white");
    $[1] = t15;
  } else {
    t15 = $[1];
  }
  const secondaryClassName = t15;
  let t16;
  if ($[2] !== className) {
    t16 = cn("py-4", className);
    $[2] = className;
    $[3] = t16;
  } else {
    t16 = $[3];
  }
  const t17 = `${name}, ${role}`;
  let t18;
  if ($[4] !== imageSrc || $[5] !== t17) {
    t18 = <LazyImage src={imageSrc} alt={t17} objectFit="cover" width={HEADSHOT_SIZE_PX} className="h-40 w-40 rounded-full ring-4 ring-orange-100 sm:h-48 sm:w-48" />;
    $[4] = imageSrc;
    $[5] = t17;
    $[6] = t18;
  } else {
    t18 = $[6];
  }
  let t19;
  if ($[7] !== name) {
    t19 = <p className="text-base font-bold text-gray-900">{name}</p>;
    $[7] = name;
    $[8] = t19;
  } else {
    t19 = $[8];
  }
  let t20;
  if ($[9] !== role) {
    t20 = <p className="text-sm text-gray-500">{role}</p>;
    $[9] = role;
    $[10] = t20;
  } else {
    t20 = $[10];
  }
  let t21;
  if ($[11] !== t19 || $[12] !== t20) {
    t21 = <figcaption className="mt-4">{t19}{t20}</figcaption>;
    $[11] = t19;
    $[12] = t20;
    $[13] = t21;
  } else {
    t21 = $[13];
  }
  let t22;
  if ($[14] !== t18 || $[15] !== t21) {
    t22 = <figure className="flex flex-col items-center text-center">{t18}{t21}</figure>;
    $[14] = t18;
    $[15] = t21;
    $[16] = t22;
  } else {
    t22 = $[16];
  }
  let t23;
  if ($[17] !== heading) {
    t23 = <h2 id="sizzle-concierge-heading" className="text-3xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-4xl">{heading}</h2>;
    $[17] = heading;
    $[18] = t23;
  } else {
    t23 = $[18];
  }
  let t24;
  if ($[19] !== description) {
    t24 = <p className="mt-4 text-base leading-relaxed text-gray-600 sm:text-lg">{description}</p>;
    $[19] = description;
    $[20] = t24;
  } else {
    t24 = $[20];
  }
  let t25;
  if ($[21] !== points) {
    t25 = points.length > 0 && <ul className="mt-6 space-y-3">{points.map(_temp)}</ul>;
    $[21] = points;
    $[22] = t25;
  } else {
    t25 = $[22];
  }
  let t26;
  if ($[23] !== enquiryLabel || $[24] !== inspirationHref || $[25] !== inspirationLabel || $[26] !== signInHref || $[27] !== submitted || $[28] !== tertiaryHref || $[29] !== tertiaryLabel || $[30] !== userSessionRequired) {
    t26 = submitted ? <div className="mt-8 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-5"><CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" /><div><p className="text-base font-bold text-green-800">Thanks, I've got it</p><p className="mt-1 text-sm text-green-800/90">I'll get back to you soon with a tailored analysis and suggestions.</p></div></div> : <><div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap"><ConciergeEnquiryButton label={enquiryLabel} signInHref={signInHref} userSessionRequired={userSessionRequired} className={primaryClassName} onSuccess={() => setSubmitted(true)} /><Link href={inspirationHref} className={secondaryClassName}><Lightbulb className="h-5 w-5" aria-hidden="true" />{inspirationLabel}</Link></div>{tertiaryLabel && tertiaryHref && <Link href={tertiaryHref} className="mt-5 inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700">{tertiaryLabel}<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>}</>;
    $[23] = enquiryLabel;
    $[24] = inspirationHref;
    $[25] = inspirationLabel;
    $[26] = signInHref;
    $[27] = submitted;
    $[28] = tertiaryHref;
    $[29] = tertiaryLabel;
    $[30] = userSessionRequired;
    $[31] = t26;
  } else {
    t26 = $[31];
  }
  let t27;
  if ($[32] !== t23 || $[33] !== t24 || $[34] !== t25 || $[35] !== t26) {
    t27 = <div>{t23}{t24}{t25}{t26}</div>;
    $[32] = t23;
    $[33] = t24;
    $[34] = t25;
    $[35] = t26;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  let t28;
  if ($[37] !== t22 || $[38] !== t27) {
    t28 = <div className="container mx-auto px-4 sm:px-6 lg:px-8"><div className="mx-auto grid max-w-5xl items-center gap-10 rounded-3xl border border-orange-100 bg-white p-8 shadow-sm ring-1 ring-orange-500/5 sm:p-12 md:grid-cols-[auto_1fr] md:gap-14">{t22}{t27}</div></div>;
    $[37] = t22;
    $[38] = t27;
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  if ($[40] !== t16 || $[41] !== t28) {
    t29 = <section className={t16} aria-labelledby="sizzle-concierge-heading">{t28}</section>;
    $[40] = t16;
    $[41] = t28;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  return t29;
}
function _temp(point) {
  return <li key={point} className="flex items-start gap-3 text-sm text-gray-700 sm:text-base"><span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>{point}</li>;
}